import {
  getHouseholdPrices,
  isValidPostcode,
} from '@getpopsure/household-insurance-pricing-engine';
import { TFunction } from '@getpopsure/i18n-react';
import { insurance } from '@getpopsure/private-constants';
import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';
import GenericCalculatorView from 'components/GenericCalculator';
import { CalculatorFieldType } from 'components/GenericCalculator/models';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

const MINIMUM_INSURED_SUM = 10_000;
const MAXIMUM_INSURED_SUM = 250_000;
const DEFAULT_INSURED_SUM = 45_000;
const SLIDER_STEP_SIZE = 1000;
const MINIMUM_MONTHLY_PREMIUM = 2.33;

const fields = (t: TFunction): CalculatorFieldType[] => [
  {
    key: 'postalCode',
    type: 'INPUT',
    props: {
      id: 'postalCode',
      type: 'text',
      label: t('household:section.calculator.postalcode.label', 'Postal code'),
      placeholder: '10961',
      pattern: '[0-9]{1,5}',
    },
  },
  {
    key: 'insuredSum',
    type: 'SLIDER',
    props: {
      defaultValue: DEFAULT_INSURED_SUM,
      label: t('household:section.calculator.slider.label', 'Insured sum'),
      min: MINIMUM_INSURED_SUM,
      max: MAXIMUM_INSURED_SUM,
      step: SLIDER_STEP_SIZE,
      modal: {
        title: t(
          'household:section.calculator.inputmodal.title',
          'Insured sum'
        ),
        description: t('household:section.calculator.inputmodal.description', {
          defaultValue:
            'Please enter your desired insured sum ranging from {{min}} to {{max}}.',
          min: englishFormattedEuroCurrency(MINIMUM_INSURED_SUM, 0),
          max: englishFormattedEuroCurrency(MAXIMUM_INSURED_SUM, 0),
        }),
        placeholder: t(
          'household:section.calculator.inputmodal.placeholder',
          'Coverage'
        ),
        belowMinErrorMessage: t(
          'household:section.calculator.inputmodal.error.belowmin',
          {
            defaultValue: 'The smallest insured sum we can offer is {{min}}',
            min: englishFormattedEuroCurrency(MINIMUM_INSURED_SUM, 0),
          }
        ),
        aboveMaxErrorMessage: t(
          'household:section.calculator.inputmodal.error.abovemax',
          {
            defaultValue:
              'The insured sum exceeds the maximum we can offer ({{max}})',
            max: englishFormattedEuroCurrency(MAXIMUM_INSURED_SUM, 0),
          }
        ),
        buttonCaption: t(
          'household:section.calculator.inputmodal.button.caption',
          'Apply'
        ),
      },
    },
  },
  {
    key: 'amount',
    type: 'MODAL',
    props: {
      label: t(
        'household:section.calculator.amountmodal.link.text',
        'What amount should I choose?'
      ),
      title: t(
        'household:section.calculator.amountmodal.link.text',
        'What amount should I choose?'
      ),
      body: t(
        'household:section.calculator.amountmodal.description',
        "The amount should be enough to cover all the belongings in your home, including appliances and furniture owned by your landlord; if you're unsure about their value, consult your landlord or make an estimate.\n\nYou can start with the most expensive items, then estimate the cost of everything else in groups — electronics, furniture, clothing, etc.\n\nIf you’re sharing a flat, the policy must cover all the rooms and everyone’s belongings. You can split the cost of the policy with your flatmates afterwards."
      ),
    },
  },
];

export const HouseholdCalculator = ({
  hasBackgroundColor,
}: {
  hasBackgroundColor: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const [price, setPrice] = useState<number | undefined>();

  const handleChange = ({ insuredSum = 0, postalCode = '' }) => {
    const householdPrices = isValidPostcode(postalCode || '')
      ? getHouseholdPrices({
          postcode: postalCode,
          propertyInSqMeters: 0,
          insuredSum: insuredSum || 0,
          hasSecurityDevice: false,
          bikeInsuredSum: 0,
        })
      : null;

    setPrice(householdPrices?.basicPremium || 0);
  };

  const showBelowMinimumFootNote = price && price > 0 && price <= 2.33;
  const priceInt = price ? Math.max(MINIMUM_MONTHLY_PREMIUM, price) : undefined;

  return (
    <GenericCalculatorView
      hasBackgroundColor={hasBackgroundColor}
      title={t('household:section.calculator.title', 'Get a price estimate')}
      cta={{
        text: t('household:section.calculator.cta.title', 'Get a quote'),
        href: insurance.household.signup,
      }}
      priceLabels={{
        topLabel: t('household:section.calculator.from', 'From'),
        bottomLabel: t('household:section.calculator.permonth', 'per month'),
        additionalInfo: showBelowMinimumFootNote
          ? t(
              'household:section.calculator.minimummonthlyplan',
              'Minimum monthly plan'
            )
          : '',
      }}
      fields={fields(t)}
      price={priceInt}
      onChange={handleChange}
    />
  );
};
